<template>
  <div class="flex-1 overflow-auto p-4">
    <div
      class="flex flex-col gap-4 mb-4 md:flex-row md:items-end md:justify-between"
    >
      <div class="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end">
        <div class="md:w-[240px] w-full multiselect-wrap--small">
          <p class="text-xs mb-2 font-medium">
            {{ $t('CONVERSATION_ATTENDANCE_REPORT.PERIOD_LABEL') }}
          </p>
          <multiselect
            v-model="selectedPreset"
            track-by="id"
            label="name"
            :options="presetOptions"
            :searchable="false"
            :allow-empty="false"
            :show-labels="false"
            @input="onPresetChange"
          />
        </div>
        <div v-if="selectedPreset.id === 'custom'" class="w-full md:w-auto">
          <p class="text-xs mb-2 font-medium">
            {{ $t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER') }}
          </p>
          <woot-date-range-picker
            show-range
            :value="customDateRange"
            :disabled-date="disableFutureDates"
            :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
            :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
            @change="onCustomRangeChange"
          />
        </div>
      </div>
      <woot-button
        color-scheme="success"
        icon="arrow-download"
        :is-loading="isExporting"
        @click="downloadCsv"
      >
        {{ $t('CONVERSATION_ATTENDANCE_REPORT.DOWNLOAD_CSV') }}
      </woot-button>
    </div>

    <div
      v-if="isLoading"
      class="flex items-center justify-center py-16 text-slate-600 dark:text-slate-300"
    >
      <spinner />
      <span class="ml-2">{{
        $t('CONVERSATION_ATTENDANCE_REPORT.LOADING')
      }}</span>
    </div>

    <div v-else-if="error" class="text-sm text-red-600 dark:text-red-400">
      {{ error }}
    </div>

    <div v-else class="space-y-6">
      <div
        class="grid grid-cols-1 gap-3 sm:grid-cols-3 bg-white dark:bg-slate-800 p-4 border border-slate-100 dark:border-slate-700 rounded-md"
      >
        <div
          class="rounded-md border border-slate-100 dark:border-slate-600 p-3"
        >
          <p
            class="text-xs font-medium text-slate-500 dark:text-slate-400 summary-title-with-info"
          >
            <span>{{ $t('CONVERSATION_ATTENDANCE_REPORT.CREATED') }}</span>
            <fluent-icon
              icon="info"
              size="14"
              class="summary-info-icon"
              v-tooltip.top="{
                content: $t('CONVERSATION_ATTENDANCE_REPORT.CREATED_INFO'),
                delay: { show: 150, hide: 100 },
                hideOnClick: true,
              }"
            />
          </p>
          <p class="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {{ accountSummary.conversations_created }}
          </p>
        </div>
        <div
          class="rounded-md border border-slate-100 dark:border-slate-600 p-3"
        >
          <p
            class="text-xs font-medium text-slate-500 dark:text-slate-400 summary-title-with-info"
          >
            <span>{{ $t('CONVERSATION_ATTENDANCE_REPORT.ASSIGNED') }}</span>
            <fluent-icon
              icon="info"
              size="14"
              class="summary-info-icon"
              v-tooltip.top="{
                content: $t('CONVERSATION_ATTENDANCE_REPORT.ASSIGNED_INFO'),
                delay: { show: 150, hide: 100 },
                hideOnClick: true,
              }"
            />
          </p>
          <p class="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {{ accountSummary.conversations_assigned }}
          </p>
        </div>
        <div
          class="rounded-md border border-slate-100 dark:border-slate-600 p-3"
        >
          <p
            class="text-xs font-medium text-slate-500 dark:text-slate-400 summary-title-with-info"
          >
            <span>{{ $t('CONVERSATION_ATTENDANCE_REPORT.UNASSIGNED') }}</span>
            <fluent-icon
              icon="info"
              size="14"
              class="summary-info-icon"
              v-tooltip.top="{
                content: $t('CONVERSATION_ATTENDANCE_REPORT.UNASSIGNED_INFO'),
                delay: { show: 150, hide: 100 },
                hideOnClick: true,
              }"
            />
          </p>
          <p class="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {{ accountSummary.conversations_unassigned }}
          </p>
        </div>
      </div>

      <div class="w-full overflow-x-auto">
        <div
          class="campaigns-table-wrap w-full min-w-[520px] rounded-lg overflow-hidden"
        >
          <table class="campaigns-woot-table conversation-attendance-report-table w-full">
            <thead>
              <tr>
                <th
                  v-for="header in tableHeaders"
                  :key="header.labelKey"
                  class="campaigns-th"
                >
                  <span class="header-with-info">
                    <span>{{ $t(header.labelKey) }}</span>
                    <fluent-icon
                      icon="info"
                      size="14"
                      class="header-info-icon"
                      v-tooltip.left="{
                        content: $t(header.tooltipKey),
                        delay: { show: 150, hide: 100 },
                        hideOnClick: true,
                      }"
                    />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in agents" :key="row.id">
                <td class="campaigns-td-title font-medium">{{ row.name }}</td>
                <td class="campaigns-td">
                  {{ row.open_in_range }}
                </td>
                <td class="campaigns-td">
                  {{ row.new_assigned_in_range }}
                </td>
                <td class="campaigns-td">
                  {{ row.resolved_in_range }}
                </td>
                <td class="campaigns-td">
                  {{ row.unattended_open }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';
import getUnixTime from 'date-fns/getUnixTime';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import Spinner from 'shared/components/Spinner.vue';
import ReportsAPI from 'dashboard/api/reports';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';

const PRESET_LAST_7 = 'last_7';
const PRESET_LAST_30 = 'last_30';
const PRESET_LAST_6_MONTHS = 'last_6_months';
const PRESET_TODAY = 'today';
const PRESET_CUSTOM = 'custom';

export default {
  name: 'ConversationAttendanceReports',
  components: {
    Multiselect,
    WootDateRangePicker,
    Spinner,
  },
  data() {
    return {
      selectedPreset: null,
      customDateRange: [new Date(), new Date()],
      since: 0,
      until: 0,
      isLoading: false,
      isExporting: false,
      error: null,
      accountSummary: {
        conversations_created: 0,
        conversations_assigned: 0,
        conversations_unassigned: 0,
      },
      agents: [],
    };
  },
  computed: {
    presetOptions() {
      return [
        {
          id: PRESET_LAST_7,
          name: this.$t('CONVERSATION_ATTENDANCE_REPORT.PRESET_LAST_7'),
        },
        {
          id: PRESET_LAST_30,
          name: this.$t('CONVERSATION_ATTENDANCE_REPORT.PRESET_LAST_30'),
        },
        {
          id: PRESET_LAST_6_MONTHS,
          name: this.$t('CONVERSATION_ATTENDANCE_REPORT.PRESET_LAST_6_MONTHS'),
        },
        {
          id: PRESET_TODAY,
          name: this.$t('CONVERSATION_ATTENDANCE_REPORT.PRESET_TODAY'),
        },
        {
          id: PRESET_CUSTOM,
          name: this.$t('CONVERSATION_ATTENDANCE_REPORT.PRESET_CUSTOM'),
        },
      ];
    },
    tableHeaders() {
      return [
        {
          labelKey: 'CONVERSATION_ATTENDANCE_REPORT.COL_AGENT',
          tooltipKey: 'CONVERSATION_ATTENDANCE_REPORT.COL_AGENT_INFO',
        },
        {
          labelKey: 'CONVERSATION_ATTENDANCE_REPORT.COL_OPEN_CONVERSATIONS',
          tooltipKey: 'CONVERSATION_ATTENDANCE_REPORT.COL_OPEN_CONVERSATIONS_INFO',
        },
        {
          labelKey: 'CONVERSATION_ATTENDANCE_REPORT.COL_NEW_ASSIGNMENTS',
          tooltipKey: 'CONVERSATION_ATTENDANCE_REPORT.COL_NEW_ASSIGNMENTS_INFO',
        },
        {
          labelKey: 'CONVERSATION_ATTENDANCE_REPORT.COL_RESOLVED',
          tooltipKey: 'CONVERSATION_ATTENDANCE_REPORT.COL_RESOLVED_INFO',
        },
        {
          labelKey: 'CONVERSATION_ATTENDANCE_REPORT.COL_UNATTENDED',
          tooltipKey: 'CONVERSATION_ATTENDANCE_REPORT.COL_UNATTENDED_INFO',
        },
      ];
    },
  },
  mounted() {
    this.selectedPreset = this.presetOptions[0];
    this.applyLast7Days();
    this.fetchReport();
  },
  methods: {
    onPresetChange(option) {
      if (!option) return;
      if (option.id === PRESET_LAST_7) this.applyLast7Days();
      else if (option.id === PRESET_LAST_30) this.applyLast30Days();
      else if (option.id === PRESET_LAST_6_MONTHS) this.applyLast6Months();
      else if (option.id === PRESET_TODAY) this.applyToday();
      else if (option.id === PRESET_CUSTOM) this.applyCustomFromPicker();
      if (option.id !== PRESET_CUSTOM) this.fetchReport();
    },
    applyLast7Days() {
      const now = new Date();
      // Seven calendar days inclusive, ending today (local)
      const start = startOfDay(subDays(now, 6));
      const end = endOfDay(now);
      this.since = getUnixTime(start);
      this.until = getUnixTime(end);
    },
    applyLast30Days() {
      const now = new Date();
      const start = startOfDay(subDays(now, 29));
      const end = endOfDay(now);
      this.since = getUnixTime(start);
      this.until = getUnixTime(end);
    },
    applyLast6Months() {
      const now = new Date();
      const start = startOfDay(subMonths(now, 6));
      const end = endOfDay(now);
      this.since = getUnixTime(start);
      this.until = getUnixTime(end);
    },
    applyToday() {
      const now = new Date();
      this.since = getUnixTime(startOfDay(now));
      this.until = getUnixTime(now);
    },
    disableFutureDates(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date > today;
    },
    onCustomRangeChange(range) {
      if (!range?.[0] || !range?.[1]) return;
      const todayEnd = endOfDay(new Date());
      let from = range[0];
      let to = range[1];
      if (to > todayEnd) to = todayEnd;
      if (from > todayEnd) from = startOfDay(new Date());
      if (startOfDay(from) > startOfDay(to)) from = startOfDay(to);
      this.customDateRange = [from, to];
      this.applyCustomFromPicker();
      this.fetchReport();
    },
    applyCustomFromPicker() {
      const [from, to] = this.customDateRange;
      this.since = getUnixTime(startOfDay(from));
      this.until = getUnixTime(endOfDay(to));
    },
    async fetchReport() {
      if (!this.since || !this.until) return;
      this.isLoading = true;
      this.error = null;
      try {
        const { data } = await ReportsAPI.getConversationAttendanceReport({
          since: this.since,
          until: this.until,
        });
        this.accountSummary = data.account || {};
        this.agents = data.agents || [];
      } catch (e) {
        this.error =
          e.response?.data?.error ||
          this.$t('CONVERSATION_ATTENDANCE_REPORT.FETCH_FAILED');
        this.agents = [];
      } finally {
        this.isLoading = false;
      }
    },
    async downloadCsv() {
      if (!this.since || !this.until) return;
      this.isExporting = true;
      try {
        const csv = await ReportsAPI.exportConversationAttendanceReport({
          since: this.since,
          until: this.until,
        });
        const fileName = `conversation-attendance-${format(
          fromUnixTime(this.until),
          'dd-MM-yyyy'
        )}.csv`;
        downloadCsvFile(fileName, csv);
      } catch (e) {
        this.error = this.$t('CONVERSATION_ATTENDANCE_REPORT.EXPORT_FAILED');
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import 'dashboard/assets/scss/campaigns-style-data-table';

.conversation-attendance-report-table {
  thead th,
  tbody td {
    text-align: center;
  }

  tbody td {
    font-variant-numeric: tabular-nums;
  }
}

.conversation-attendance-report-table .header-with-info {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

.conversation-attendance-report-table .header-info-icon {
  color: var(--s-500);
  cursor: pointer;
}

.summary-title-with-info {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

.summary-info-icon {
  color: var(--s-500);
  cursor: pointer;
}
</style>
